// UI-side mirror of packages/core/src/permissions.ts for human principals. The server is the authority and enforces
// every rule; this copy only decides what to show (nav entries, New / Edit / Archive buttons, read-only fields).
// permissions.parity.test.ts compares it with the server rules for every role and module, so drift fails CI.
import type { EntitySchema, FieldSchema, LinkSchema } from './schema'

export const ROLES = [
  'ADMIN',
  'INFOSEC_LEAD',
  'CONTROL_MANAGER',
  'POLICY_MANAGER',
  'PEOPLE_OPS',
  'RISK_MANAGER',
  'TRUST_CENTER_MANAGER',
  'ACCESS_REVIEWER',
  'AUDITOR',
  'EMPLOYEE',
  'READ_ONLY',
] as const
export type Role = (typeof ROLES)[number]

const STAFF_ADMINS: Role[] = ['ADMIN', 'INFOSEC_LEAD']

export const MODULE_WRITERS: Record<string, Role[]> = {
  frameworks: [...STAFF_ADMINS, 'CONTROL_MANAGER'],
  controls: [...STAFF_ADMINS, 'CONTROL_MANAGER'],
  evidence: [...STAFF_ADMINS, 'CONTROL_MANAGER', 'POLICY_MANAGER', 'PEOPLE_OPS', 'RISK_MANAGER'],
  monitoring: [...STAFF_ADMINS, 'CONTROL_MANAGER'],
  connections: [...STAFF_ADMINS],
  policies: [...STAFF_ADMINS, 'POLICY_MANAGER'],
  personnel: [...STAFF_ADMINS, 'PEOPLE_OPS'],
  devices: [...STAFF_ADMINS, 'PEOPLE_OPS'],
  assets: [...STAFF_ADMINS, 'PEOPLE_OPS', 'RISK_MANAGER'],
  'access-reviews': [...STAFF_ADMINS, 'PEOPLE_OPS', 'ACCESS_REVIEWER'],
  vendors: [...STAFF_ADMINS, 'RISK_MANAGER'],
  risks: [...STAFF_ADMINS, 'RISK_MANAGER'],
  vulnerabilities: [...STAFF_ADMINS, 'RISK_MANAGER', 'CONTROL_MANAGER'],
  tasks: [
    ...STAFF_ADMINS,
    'CONTROL_MANAGER',
    'POLICY_MANAGER',
    'PEOPLE_OPS',
    'RISK_MANAGER',
    'TRUST_CENTER_MANAGER',
  ],
  audits: [...STAFF_ADMINS, 'CONTROL_MANAGER'],
  events: [],
  trust: [...STAFF_ADMINS, 'TRUST_CENTER_MANAGER'],
  settings: ['ADMIN'],
  core: [
    ...STAFF_ADMINS,
    'CONTROL_MANAGER',
    'POLICY_MANAGER',
    'PEOPLE_OPS',
    'RISK_MANAGER',
    'TRUST_CENTER_MANAGER',
  ],
  notifications: [],
  // Security-bot findings. Same two roles that may read the module (MODULE_READERS).
  maintenance: [...STAFF_ADMINS],
  // Parallel-run report. Same two roles that may read the module (MODULE_READERS).
  parallel: [...STAFF_ADMINS],
  // Compliancebot fixes. Same two roles that may read the module and decide a fix (MODULE_READERS).
  remediation: [...STAFF_ADMINS],
}

export const AUDITOR_READ = new Set([
  'frameworks',
  'controls',
  'evidence',
  'monitoring',
  'policies',
  'audits',
  'core',
])

/**
 * Narrow staff roles that read only their own modules (Drata page access per role). Access Reviewer: Access Reviews,
 * Connections read-only, and `core` so the Notes tab of those records loads (the server shows only the notes on
 * entity types the caller can read). They write Access Reviews only. Same table as the server's ROLE_READ.
 */
export const ROLE_READ: Partial<Record<Role, ReadonlySet<string>>> = {
  ACCESS_REVIEWER: new Set(['access-reviews', 'connections', 'core']),
}

/** Where a narrow role lands when it opens the app root (its first page; the dashboard is for full staff). */
const ROLE_HOME: Partial<Record<Role, string>> = {
  ACCESS_REVIEWER: '/access-reviews',
}

export const STAFF_READ: Role[] = [
  'ADMIN',
  'INFOSEC_LEAD',
  'CONTROL_MANAGER',
  'POLICY_MANAGER',
  'PEOPLE_OPS',
  'RISK_MANAGER',
  'TRUST_CENTER_MANAGER',
  'READ_ONLY',
]

/**
 * Modules that only some staff may read. Same table as the server's MODULE_READERS.
 * Maintenance tickets hold security-bot findings, so only Admin and InfoSec Lead see the inbox.
 * The parallel-run report is the same two roles.
 * Remediation proposals are the same two roles: they approve or reject the fix.
 */
export const MODULE_READERS: Record<string, readonly Role[]> = {
  maintenance: ['ADMIN', 'INFOSEC_LEAD'],
  parallel: ['ADMIN', 'INFOSEC_LEAD'],
  remediation: ['ADMIN', 'INFOSEC_LEAD'],
}

/** The signed-in person as far as permissions are concerned. */
export interface Viewer {
  roles: readonly string[]
  isGuest?: boolean
}

const has = (v: Viewer, roles: readonly Role[]) =>
  v.roles.some((r) => (roles as readonly string[]).includes(r))

export function isStaff(v: Viewer): boolean {
  return has(v, STAFF_READ)
}

export function isAuditor(v: Viewer): boolean {
  return !isStaff(v) && (v.roles.includes('AUDITOR') || !!v.isGuest)
}

/** Holds a narrow staff role (Access Reviewer): staff for that role's pages only. */
export function hasNarrowRole(v: Viewer): boolean {
  return v.roles.some((r) => ROLE_READ[r as Role] !== undefined)
}

/**
 * Reads access reviews only through the Access Reviewer role (the server's isAccessReviewerOnly in
 * packages/modules/src/access-reviews/scope.ts): such a person decides the accounts of the applications they are
 * assigned to, while managing a review (start, refresh accounts, edit applications, sign off) is refused with 403.
 */
export function isAccessReviewerOnly(v: Viewer): boolean {
  if (!v.roles.includes('ACCESS_REVIEWER')) return false
  const others = v.roles.filter((r) => r !== 'ACCESS_REVIEWER')
  return !canReadModule({ roles: others }, 'access-reviews')
}

/**
 * Manages access reviews (Start review, Refresh accounts, Add application, assign reviewers, Complete review sign-off):
 * writes the module through a role other than Access Reviewer, so Admin, InfoSec Lead or People Ops. Write rights
 * decide, not read rights: a Read Only or Control Manager who is also an Access Reviewer reads every review but manages
 * none. Mirrors the server's assertManager / writesOnlyAsAccessReviewer in packages/modules/src/access-reviews/scope.ts.
 * Deciding accounts and completing an application stay with the application's reviewer.
 */
export function managesAccessReviews(v: Viewer): boolean {
  return canWriteModule({ roles: v.roles.filter((r) => r !== 'ACCESS_REVIEWER') }, 'access-reviews')
}

/**
 * People with only the EMPLOYEE role (or no role yet) see just My Compliance, like Drata's "limited access" users.
 * A narrow role (Access Reviewer) is not employee-only: it gets its own pages in the sidebar.
 */
export function isEmployeeOnly(v: Viewer): boolean {
  return !isStaff(v) && !isAuditor(v) && !hasNarrowRole(v)
}

/**
 * The page the app root (/) should show this person: the dashboard for staff and auditors (it links auditors to their
 * portal), My Compliance for employees, and the first page of a narrow role (an Access Reviewer opens Access Reviews).
 */
export function homePath(v: Viewer): string {
  if (isStaff(v) || isAuditor(v)) return '/'
  for (const r of v.roles) {
    const home = ROLE_HOME[r as Role]
    if (home) return home
  }
  return '/me'
}

export function canReadModule(v: Viewer, module: string): boolean {
  const restricted = MODULE_READERS[module]
  if (restricted) return has(v, restricted)
  if (isStaff(v)) return true
  if (v.roles.some((r) => ROLE_READ[r as Role]?.has(module))) return true
  if (v.roles.includes('AUDITOR') || v.isGuest) return AUDITOR_READ.has(module)
  return false
}

export function canWriteModule(v: Viewer, module: string): boolean {
  return has(v, MODULE_WRITERS[module] ?? STAFF_ADMINS)
}

export interface EntityAccess {
  read: boolean
  create: boolean
  update: boolean
  archive: boolean
  /** May restore an archived row (the server's canRestore; an archive can be final, e.g. compliance exclusions). */
  restore: boolean
}

/** What the viewer may do with an entity. Server-provided permissions (if the schema includes them) win. */
export function entityAccess(v: Viewer, e: EntitySchema): EntityAccess {
  const read = canReadModule(v, e.module)
  const write = canWriteModule(v, e.module)
  const computed: EntityAccess = {
    read,
    create: write && !e.systemCreateOnly,
    update: write && !e.immutable,
    archive: write && !e.immutable,
    restore: write && !e.immutable,
  }
  const p = e.permissions
  if (!p) return computed
  return {
    read: p.read ?? computed.read,
    create: p.create ?? computed.create,
    update: p.update ?? computed.update,
    archive: p.archive ?? computed.archive,
    // Not p.archive: a permanent entity (archive false) may still restore old archived rows, and a final archive
    // (restore false) must not offer a Restore the server answers with 403.
    restore: p.restore ?? computed.restore,
  }
}

/**
 * What the archive confirmation tells people happens next. Only promise a restore the caller can do: when the
 * server says archived rows cannot be restored (compliance exclusions), the archive is permanent.
 */
export function archiveConsequence(
  access: Pick<EntityAccess, 'restore'>,
  opts: { many?: boolean } = {},
): string {
  const them = opts.many ? 'them' : 'it'
  const base = 'Archived records are hidden from lists and cannot be edited.'
  if (!access.restore)
    return `${base} This archive is permanent: you cannot restore ${them} afterwards. Create a new record instead if you need ${them} again.`
  return opts.many
    ? `${base} Nothing is deleted: you can restore them at any time from Show archived.`
    : `${base} Nothing is deleted: you can restore it at any time.`
}

export type ReadOnlyReason = 'system' | 'immutable' | 'archived' | 'no-permission'

export interface FieldEditability {
  editable: boolean
  reason: ReadOnlyReason | null
  /** Only a person can change it; agents get a 403. Shown with a lock icon. */
  humanOnly: boolean
}

export function fieldEditability(
  v: Viewer,
  e: EntitySchema,
  f: FieldSchema,
  opts: { archived?: boolean } = {},
): FieldEditability {
  const humanOnly = f.access === 'human'
  if (f.access === 'system') return { editable: false, reason: 'system', humanOnly }
  if (e.immutable) return { editable: false, reason: 'immutable', humanOnly }
  if (opts.archived) return { editable: false, reason: 'archived', humanOnly }
  if (!entityAccess(v, e).update) return { editable: false, reason: 'no-permission', humanOnly }
  const allowed = e.permissions?.writableFields
  if (allowed && !allowed.includes(f.name)) return { editable: false, reason: 'no-permission', humanOnly }
  return { editable: true, reason: null, humanOnly }
}

/** Whether the viewer may map and unmap records through a link. Server-provided permission wins. */
export function canLink(v: Viewer, link: LinkSchema | undefined): boolean {
  if (!link) return false
  return link.permissions?.canLink ?? canWriteModule(v, link.module)
}

/** Fields a person may set when creating a record (system fields are computed by the platform). */
export function creatableFields(e: EntitySchema): FieldSchema[] {
  const allowed = e.permissions?.creatableFields
  return e.fields.filter((f) => f.access !== 'system' && (!allowed || allowed.includes(f.name)))
}

/** Short explanation of the role needed to write a module, used in "you cannot edit" hints. */
export function writersHint(module: string): string {
  const roles = MODULE_WRITERS[module] ?? STAFF_ADMINS
  if (!roles.length) return 'Records here are written by the platform only.'
  const labels = roles.map((r) =>
    r === 'INFOSEC_LEAD'
      ? 'InfoSec Lead'
      : r.charAt(0) +
        r
          .slice(1)
          .toLowerCase()
          .replace(/_(\w)/g, (_, c) => ` ${c.toUpperCase()}`),
  )
  return `Requires one of these roles: ${labels.join(', ')}.`
}
